import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import type { LogAction, LogEntry } from "../types"
import { api } from "../lib/api"
import { useAuth } from "./AuthProvider"

interface LogContextValue {
  logs: LogEntry[]
  refetch: () => Promise<void>
  logAction: (
    page: string,
    action: LogAction,
    details: string,
    client?: string,
    clientId?: string,
  ) => void
}

const LogContext = createContext<LogContextValue>({
  logs: [],
  refetch: async () => {},
  logAction: () => {},
})

export function LogProvider({ children }: { children: ReactNode }) {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const { user } = useAuth()

  const fetchLogs = async () => {
    try {
      const records = await api.getLogs()
      setLogs(
        records.map((r) => ({
          id: r.id,
          timestamp: new Date(r.timestamp).toLocaleString("uk-UA"),
          user: r.user,
          page: r.page,
          action: r.action as LogAction,
          details: r.details,
          client: r.client || undefined,
          clientId: r.clientId || undefined,
          accountEmail: r.accountEmail || undefined,
        })),
      )
    } catch {}
  }

  useEffect(() => {
    fetchLogs()
  }, [])

  const logAction = (
    page: string,
    action: LogAction,
    details: string,
    client?: string,
    clientId?: string,
  ) => {
    const entry: LogEntry = {
      id: Date.now(),
      timestamp: new Date().toLocaleString("uk-UA"),
      user: user?.fullName ?? "",
      page,
      action,
      details,
      client,
      clientId,
    }
    setLogs((prev) => [entry, ...prev])
    api
      .createLog({
        user: user?.fullName ?? "",
        page,
        action,
        details,
        client,
        clientId,
      })
      .catch(() => {})
  }

  return (
    <LogContext.Provider value={{ logs, refetch: fetchLogs, logAction }}>
      {children}
    </LogContext.Provider>
  )
}

export const useLogs = () => useContext(LogContext)
